// src/pages/admin/billing/AdminBillingCustomersPage.js
import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminData } from '../../../contexts/AdminDataContext';
import QuickCustomerForm from '../../../components/admin/billing/quote/QuickCustomerForm';
import QuickVehicleForm from '../../../components/admin/billing/quote/QuickVehicleForm';
import QuoteForm from '../../../components/admin/billing/quote/QuoteForm';
import InvoiceForm from '../../../components/admin/billing/invoice/InvoiceForm';
import ContractForm from '../../../components/admin/billing/contracts/ContractForm';
import PDFGenerator from '../../../utils/PDFGenerator';
import pb from '../../../lib/pocketbase';
import { useNavigate } from 'react-router-dom';

function AdminBillingCustomersPage() {
  const navigate = useNavigate();
  const { loading: adminLoading, customers: ctxCustomers, projects: ctxProjects, services: ctxServices, products: ctxProducts } = useAdminData() || {};
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [showCustomerForm, setShowCustomerForm] = React.useState(false);
  const [editingCustomer, setEditingCustomer] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = React.useState('');
  const [showAddMenuFor, setShowAddMenuFor] = React.useState(null);

  const [customers, setCustomers] = React.useState(() => Array.isArray(ctxCustomers) ? ctxCustomers : []);
  const [projects, setProjects] = React.useState(() => Array.isArray(ctxProjects) ? ctxProjects : []);
  const [services, setServices] = React.useState(() => Array.isArray(ctxServices) ? ctxServices : []);
  const [products, setProducts] = React.useState(() => Array.isArray(ctxProducts) ? ctxProducts : []);

  const [settings, setSettings] = React.useState({});
  const [error, setError] = React.useState('');
  const [showProjectForm, setShowProjectForm] = React.useState(false);
  const [editingProject, setEditingProject] = React.useState(null);
  const [selectedCustomerForProject, setSelectedCustomerForProject] = React.useState(null);
  const [showCustomerDetail, setShowCustomerDetail] = React.useState(false);
  const [detailCustomer, setDetailCustomer] = React.useState(null);
  const [detailQuotes, setDetailQuotes] = React.useState([]);
  const [detailInvoices, setDetailInvoices] = React.useState([]);
  const [detailContracts, setDetailContracts] = React.useState([]);
  const [detailAppointments, setDetailAppointments] = React.useState([]);
  const [showProjectDetail, setShowProjectDetail] = React.useState(false);
  const [detailProject, setDetailProject] = React.useState(null);
  const [showQr, setShowQr] = React.useState(false);
  const [qrUrl, setQrUrl] = React.useState('');

  // States for sub-forms
  const [showQuoteForm, setShowQuoteForm] = React.useState(false);
  const [editingQuote, setEditingQuote] = React.useState(null);
  const [showInvoiceForm, setShowInvoiceForm] = React.useState(false);
  const [editingInvoice, setEditingInvoice] = React.useState(null);
  const [showContractForm, setShowContractForm] = React.useState(false);
  const [editingContract, setEditingContract] = React.useState(null);

  // -- Password Reset Modal State --
  const [showPwdModal, setShowPwdModal] = React.useState(false);
  const [pwdStep, setPwdStep] = React.useState('input'); // input | superadmin | success
  const [pwdTargetUser, setPwdTargetUser] = React.useState(null);      // { id, name, email, customerId }
  const [pwdNewPassword, setPwdNewPassword] = React.useState('');
  const [pwdAdminEmail, setPwdAdminEmail] = React.useState('');
  const [pwdAdminPass, setPwdAdminPass] = React.useState('');
  const [pwdLoading, setPwdLoading] = React.useState(false);
  const [pwdError, setPwdError] = React.useState('');

  const companyName = 'INFOCOLAB';
  const buildShareText = (link) => (
    `Bonjour,\n\n` +
    `pour créer votre dossier client et démarrer votre projet, merci de compléter ce formulaire : ${link}\n` +
    `Cela prend moins de 3 minutes.\n` +
    `Si vous avez la moindre question, répondez simplement à ce message — nous sommes là pour vous accompagner.\n\n` +
    `Bien à vous,\n` +
    `L’équipe ${companyName}`
  );

  const getProjectsByCustomer = (customerId) => {
    if (!customerId) return [];
    return projects.filter(p => (
      p?.client === customerId || // nouveau schéma
      p?.customer_id === customerId || // compat ancien
      p?.customer === customerId // compat intermédiaire
    ));
  };
  const formatDate = (iso) => new Date(iso).toLocaleDateString();
  const statusLabel = (s) => ({ draft: 'Brouillon', active: 'Actif', paused: 'En pause', done: 'Terminé' }[s] || '—');

  const fetchCustomers = async () => {
    setError('');
    try {
      const res = await pb.collection('customers').getList(1, 100, { sort: '-created', expand: 'user' });
      setCustomers(res?.items ?? []);
    } catch (e) {
      setError(e?.message || 'Erreur lors du chargement des clients');
      setCustomers([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await pb.collection('projects').getList(1, 100, { sort: '-created' });
      setProjects(res?.items ?? []);
    } catch {
      setProjects([]);
    }
  };

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      await Promise.all([fetchCustomers(), fetchProjects()]);
      if (mounted) setLoading(false);
    })();

    // Realtime updates (track subscriptions to safely unsubscribe later)
    let subCustomers = false;
    let subProjects = false;
    try {
      pb.collection('customers')
        .subscribe('*', () => { fetchCustomers(); })
        .then(() => { subCustomers = true; })
        .catch(() => { });
    } catch { }
    try {
      pb.collection('projects')
        .subscribe('*', () => { fetchProjects(); })
        .then(() => { subProjects = true; })
        .catch(() => { });
    } catch { }

    return () => {
      mounted = false;
      try { if (subCustomers) { pb.collection('customers').unsubscribe('*').catch(() => { }); } } catch { }
      try { if (subProjects) { pb.collection('projects').unsubscribe('*').catch(() => { }); } } catch { }
    };
  }, []);

  // Seed from global cache instantly (prevents flash)
  React.useEffect(() => {
    if (Array.isArray(ctxCustomers) && ctxCustomers.length) setCustomers(ctxCustomers);
    if (Array.isArray(ctxProjects) && ctxProjects.length) setProjects(ctxProjects);
    if (Array.isArray(ctxServices)) setServices(ctxServices);
    if (Array.isArray(ctxProducts)) setProducts(ctxProducts);
  }, [ctxCustomers, ctxProjects, ctxServices, ctxProducts]);

  // Prevent empty-state flash on quick tab switches
  React.useEffect(() => {
    const t = setTimeout(() => setAllowEmpty(true), 200);
    return () => { clearTimeout(t); setAllowEmpty(false); };
  }, []);

  // Charger les devis/factures/rdv à l'ouverture du détail client
  React.useEffect(() => {
    const loadLinked = async () => {
      if (!showCustomerDetail || !detailCustomer?.id) return;
      try {
        const [qRes, iRes, aRes, cRes, sRes] = await Promise.all([
          pb.collection('quotes').getList(1, 50, { sort: '-created', filter: `customer_id = "${detailCustomer.id}"` }).catch(() => ({ items: [] })),
          pb.collection('invoices').getList(1, 50, { sort: '-created', filter: `customer_id = "${detailCustomer.id}"` }).catch(() => ({ items: [] })),
          pb.collection('appointments').getList(1, 50, { sort: '-created', filter: `customer_id = "${detailCustomer.id}"` }).catch(() => ({ items: [] })),
          pb.collection('contracts').getList(1, 50, { sort: '-created', filter: `customer_id = "${detailCustomer.id}"` }).catch(() => ({ items: [] })),
          pb.collection('settings').getFirstListItem('').catch(() => ({})),
        ]);
        setDetailQuotes(qRes?.items ?? []);
        setDetailInvoices(iRes?.items ?? []);
        setDetailAppointments(aRes?.items ?? []);
        setDetailContracts(cRes?.items ?? []);
        if (sRes) setSettings(sRes);
      } catch {
        setDetailQuotes([]); setDetailInvoices([]); setDetailAppointments([]); setDetailContracts([]);
      }
    };
    loadLinked();
  }, [showCustomerDetail, detailCustomer?.id]);

  const handleCreateOrUpdate = async (data) => {
    setLoading(true);
    setError('');
    try {
      if (editingCustomer?.id) {
        await pb.collection('customers').update(editingCustomer.id, data);
      } else {
        await pb.collection('customers').create(data);
      }
      await fetchCustomers();
      setShowCustomerForm(false);
      setEditingCustomer(null);
      return { success: true };
    } catch (e) {
      setError(e?.message || "Erreur lors de l'enregistrement du client");
      return { success: false, error: e?.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteCustomer = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await pb.collection('customers').delete(id);
      await fetchCustomers();
    } catch (e) {
      alert(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handlePasswordReset = async (customer) => {
    // 1. Resolve User ID
    let userId = customer.expand?.user?.id || customer.user;

    // A. Link logic (still uses standard confirm/alert for the linking part as it is a prerequisite)
    if (!userId) {
      if (customer.email) {
        try {
          const existing = await pb.collection('users').getFirstListItem(`email="${customer.email}"`);
          if (existing) {
            await pb.collection('customers').update(customer.id, { user: existing.id });
            userId = existing.id;
          }
        } catch { }
      }
      if (!userId) {
        if (window.confirm(`⚠️ Pas de compte utilisateur pour ${customer.name}. Créer maintenant ?`)) {
          const email = customer.email || prompt("Email pour le compte :");
          const pw = prompt("Mot de passe initial :");
          if (!email || !pw) return;
          try {
            const u = await pb.collection('users').create({
              email, password: pw, passwordConfirm: pw, name: customer.name, role: 'client', emailVisibility: true
            });
            await pb.collection('customers').update(customer.id, { user: u.id });
            alert(`✅ Compte créé ! (MDP : ${pw})`);
            await fetchCustomers();
            return;
          } catch (e) { alert(e.message); return; }
        } else return;
      }
    }

    // 2. Open Styled Modal
    setPwdTargetUser({ id: userId, name: customer.name, email: customer.email, customerId: customer.id });
    setPwdNewPassword('');
    setPwdError('');
    setPwdStep('input');
    setPwdAdminEmail('');
    setPwdAdminPass('');
    setShowPwdModal(true);
  };

  const executePasswordReset = async () => {
    if (pwdNewPassword.length < 8) {
      setPwdError("Le mot de passe doit faire au moins 8 caractères.");
      return;
    }
    setPwdLoading(true);
    setPwdError('');
    try {
      await pb.collection('users').update(pwdTargetUser.id, {
        password: pwdNewPassword,
        passwordConfirm: pwdNewPassword
      });
      setPwdStep('success');
    } catch (err) {
      // Detect OldPassword Requirement
      const isKeyMissing = err?.data?.data?.oldPassword?.code === 'validation_required' || err?.message?.includes('oldPassword');
      if (isKeyMissing) {
        setPwdStep('superadmin'); // Switch to Super Admin auth step
      } else {
        setPwdError(err.message || 'Erreur inconnue');
      }
    } finally {
      setPwdLoading(false);
    }
  };

  const executeSuperAdminReset = async () => {
    if (!pwdAdminEmail || !pwdAdminPass) {
      setPwdError("Veuillez remplir les identifiants Super-Admin.");
      return;
    }
    setPwdLoading(true);

    // Create a segregated instance to avoid disturbing the global auth state
    // This prevents the app from detecting a "user change" and redirecting
    const tempPb = new pb.constructor(pb.baseUrl);

    try {
      // 1. Auth as Admin on temp instance
      await tempPb.admins.authWithPassword(pwdAdminEmail, pwdAdminPass);

      // 2. Update using temp instance
      await tempPb.collection('users').update(pwdTargetUser.id, {
        password: pwdNewPassword,
        passwordConfirm: pwdNewPassword
      });

      // 3. Success
      setPwdStep('success');
    } catch (err) {
      console.error(err);
      setPwdError("Échec Super-Admin : " + (err.message || "Identifiants incorrects ?"));
    } finally {
      // No need to restore auth, global 'pb' was never touched
      setPwdLoading(false);
    }
  };

  const handleDownloadQuote = async (quote) => {
    if (!quote) return;
    try {
      const generator = new PDFGenerator();
      // Find project associated with quote if any
      const project = projects.find(p => p.id === quote.project_id) || null;
      const doc = await generator.generateQuotePDF(quote, detailCustomer, project, settings);
      doc.save(`Devis-${quote.quote_number || quote.id}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la génération du PDF: " + (err.message || err));
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    if (!invoice) return;
    try {
      const generator = new PDFGenerator();
      const project = projects.find(p => p.id === invoice.project_id) || null;
      const doc = await generator.generateInvoicePDF(invoice, detailCustomer, project, settings);
      doc.save(`Facture-${invoice.invoice_number || invoice.id}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la génération du PDF: " + (err.message || err));
    }
  };

  const handleDownloadContract = async (contract) => {
    if (!contract) return;
    try {
      const generator = new PDFGenerator();
      const doc = await generator.generateContractPDF(contract, settings);
      doc.save(`Contrat-${contract.contract_number || contract.id}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la génération du PDF: " + (err.message || err));
    }
  };

  const handleViewQuote = async (quote) => {
    if (!quote) return;
    try {
      const generator = new PDFGenerator();
      const project = projects.find(p => p.id === quote.project_id) || null;
      const doc = await generator.generateQuotePDF(quote, detailCustomer, project, settings);
      window.open(doc.output('bloburl'), '_blank');
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la génération du PDF: " + (err.message || err));
    }
  };

  const handleViewInvoice = async (invoice) => {
    if (!invoice) return;
    try {
      const generator = new PDFGenerator();
      const project = projects.find(p => p.id === invoice.project_id) || null;
      const doc = await generator.generateInvoicePDF(invoice, detailCustomer, project, settings);
      window.open(doc.output('bloburl'), '_blank');
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la génération du PDF: " + (err.message || err));
    }
  };

  const handleViewContract = async (contract) => {
    if (!contract) return;
    try {
      const generator = new PDFGenerator();
      const doc = await generator.generateContractPDF(contract, settings);
      window.open(doc.output('bloburl'), '_blank');
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la génération du PDF: " + (err.message || err));
    }
  };

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ marginTop: 0 }}>Gestion des clients</h1>
        <button
          className="btn-primary"
          onClick={() => { setEditingCustomer(null); setShowCustomerForm(true); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderRadius: 10,
            background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
            border: '1px solid rgba(79,70,229,0.6)',
            boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
            color: 'white'
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </span>
          <span>Nouveau client</span>
        </button>
      </div>
      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .header-pill--square{ border-radius:10px; border:1px solid var(--admin-border); color: var(--admin-text); background: transparent; padding:8px 10px; }
        .header-pill--square:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .add-menu{ position:absolute; top: calc(100% + 8px); right:0; min-width: 200px; z-index: 60; background: linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2)); border:1px solid var(--admin-border); border-radius: 12px; padding:6px; box-shadow: 0 20px 40px rgba(0,0,0,0.25); }
        .add-menu::before{ content:""; position:absolute; top:-6px; left:8px; width:10px; height:10px; background: linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2)); border-left:1px solid var(--admin-border); border-top:1px solid var(--admin-border); transform: rotate(45deg); pointer-events:none; }
        .add-menu-item{ background: transparent; display:flex; align-items:center; gap:10px; width:100%; text-align:left; padding:10px 12px; border-radius:10px; color: var(--admin-text); border:1px solid transparent; transition: background .15s ease, color .15s ease; cursor:pointer; }
        .add-menu-item:hover{ background: rgba(255,255,255,0.08); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .add-menu-item:hover{ background: rgba(2,6,23,0.06); }

        /* Modal styles to match Quote/Invoice quick forms */
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 20px 0 20px; border-bottom: 1px solid var(--admin-border); margin-bottom: 16px; }
        .modal-header h3 { margin: 0; font-size: 1.2rem; font-weight: 600; color: var(--admin-text); }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 6px; border-radius: 6px; transition: all .2s ease; display:inline-flex; align-items:center; justify-content:center; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .modal-actions { display: flex; justify-content: flex-end; gap: 12px; padding: 0 20px 20px 20px; border-top: 1px solid var(--admin-border); margin-top: 16px; padding-top: 16px; }
        .btn-secondary { background: var(--admin-panel-2); color: var(--admin-text); border: 1px solid var(--admin-border); padding: 10px 16px; border-radius: 8px; cursor: pointer; }
        .btn-secondary:hover { background: rgba(255,255,255,0.06); }
        .btn-primary { background: var(--admin-accent); color: #fff; border: none; padding: 10px 16px; border-radius: 8px; cursor: pointer; }
        .btn-primary:disabled { opacity: .6; cursor: not-allowed; }

        /* Quick vehicle (project) form spacing */
        .quick-vehicle-modal { max-width: 800px; width: 90vw; }
        .quick-vehicle-form { padding: 24px; }
        .quick-vehicle-form .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 24px; }
        .quick-vehicle-form .form-actions { display: flex; justify-content: flex-end; gap: 12px; padding-top: 20px; border-top: 1px solid var(--admin-border); margin-top: 24px; }
        .quick-vehicle-form .form-group { margin-bottom: 4px; }
        .quick-vehicle-form label { display: block; margin-bottom: 6px; font-weight: 500; color: var(--admin-text); font-size: 0.875rem; }
        .quick-vehicle-form input, .quick-vehicle-form select, .quick-vehicle-form textarea { width: 100%; padding: 10px 12px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; }
        .quick-vehicle-form input:focus, .quick-vehicle-form select:focus, .quick-vehicle-form textarea:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }

        /* Avatars (même style qu'admin facture) */
        .customer-avatar { width:36px; height:36px; border-radius:999px; display:grid; place-items:center; font-weight:700; }
        .admin-shell .customer-avatar { background:#3b82f6 !important; color:#fff !important; }
        /* Shape-only modifier for header buttons (no color change) */
        .header-shape{ border-radius:10px; padding:8px 10px; }
        /* Couleurs pour actions client (œil, crayon, supprimer) */
        .btn-muted{ background: var(--admin-panel); border:1px solid var(--admin-border); color: var(--admin-text); }
        /* Override generic .header-pill:hover to keep neutral hover subtle */
        .btn-muted:hover{ background: transparent !important; border-color: var(--admin-border); color: var(--admin-text); box-shadow: none; }
        .btn-accent{ background: rgba(79,70,229,0.12); color: var(--admin-accent); border:1px solid rgba(79,70,229,0.45); }
        .btn-accent:hover{ background: rgba(79,70,229,0.22) !important; border-color: rgba(79,70,229,0.6) !important; color: var(--admin-accent) !important; }
        .btn-danger{ background: rgba(239,68,68,0.12); color:#ef4444; border:1px solid rgba(239,68,68,0.45); }
        .btn-danger:hover{ background: rgba(239,68,68,0.22) !important; border-color: rgba(239,68,68,0.6) !important; color:#ef4444 !important; }
        .btn-success{ background: rgba(34,197,94,0.12); color:#16a34a; border:1px solid rgba(34,197,94,0.45); }
        .btn-success:hover{ background: rgba(34,197,94,0.22) !important; border-color: rgba(34,197,94,0.6) !important; color:#16a34a !important; }
        .btn-warning{ background: rgba(234,179,8,0.14); color:#eab308; border:1px solid rgba(234,179,8,0.45); }
        .btn-warning:hover{ background: rgba(234,179,8,0.18); }

        /* Simple tooltip on hover for action buttons */
        .has-tip{ position:relative; }
        .has-tip::after{
          content: attr(data-tip);
          position:absolute; bottom: calc(100% + 8px); left:50%; transform: translateX(-50%);
          background: var(--admin-panel); color: var(--admin-text);
          border:1px solid var(--admin-border); border-radius:8px; padding:6px 8px; font-size:.8rem; white-space:nowrap;
          box-shadow: 0 8px 18px rgba(0,0,0,0.18);
          opacity:0; pointer-events:none; transition: opacity .15s ease;
          z-index: 70;
        }
        .has-tip::before{
          content:""; position:absolute; bottom: calc(100% + 4px); left:50%; transform: translateX(-50%) rotate(45deg);
          width:10px; height:10px; background: var(--admin-panel);
          border-left:1px solid var(--admin-border); border-top:1px solid var(--admin-border);
          opacity:0; transition: opacity .15s ease; z-index: 70;
        }
        .has-tip:hover::after, .has-tip:hover::before{ opacity:1; }

        /* Search bar (style modèle) */
        .searchbar{ position:relative; display:flex; align-items:center; gap:8px; }
        .searchbar .icon{ position:absolute; left:10px; color: var(--admin-muted); }
        .searchbar input{ width:260px; background: var(--admin-panel); border:1px solid var(--admin-border); color: var(--admin-text); border-radius:10px; padding:8px 10px 8px 30px; outline:none; transition: box-shadow .15s ease, border-color .15s ease; }
        .searchbar input::placeholder{ color: var(--admin-muted); }
        .searchbar input:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
      `}</style>

      <div onClick={() => setShowAddMenuFor(null)}>
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="header-pill header-pill--square" title="QR Onboarding" aria-label="QR Onboarding" onClick={() => {
                const link = window.location.origin + '/inscription';
                setQrUrl(link);
                setShowQr(true);
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z" />
                </svg>
              </button>
              <button className="header-pill header-pill--square" onClick={async () => {
                const link = window.location.origin + '/inscription';
                try { await navigator.clipboard.writeText(link); alert('Lien copié'); } catch { }
              }}>Copier le lien</button>
              <button className="header-pill header-pill--square" onClick={async () => {
                const link = window.location.origin + '/inscription';
                const shareText = buildShareText(link);
                try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { }
              }}>Copier le message</button>
              <button className="header-pill header-pill--square" onClick={async () => {
                const link = window.location.origin + '/inscription';
                const shareText = buildShareText(link);
                if (navigator.share) {
                  try { await navigator.share({ title: 'Inscription', text: shareText }); } catch { }
                } else {
                  try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { alert('Partage non supporté'); }
                }
              }}>Partager</button>
            </div>
            <div className="searchbar" style={{ marginLeft: 'auto' }}>
              <svg className="icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
              <input
                type="text"
                placeholder="Rechercher un client..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
              />
            </div>
          </div>

        </div>
        {/* Customers grid */}
        <div className="card" style={{ marginTop: 12 }}>
          {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
            {customers
              .filter(c => c.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
                (c.email && c.email.toLowerCase().includes(customerSearchTerm.toLowerCase())) ||
                (c.phone && c.phone.includes(customerSearchTerm)))
              .map((customer) => (
                <div key={customer.id} style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 12, background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="customer-avatar" style={{ backgroundColor: '#2d3556', color: '#fff' }}>
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{customer.name}</div>
                      {customer.company && <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>{customer.company}</div>}
                    </div>
                  </div>

                  <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                    {customer.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{customer.email}</span>
                      </div>
                    )}
                    {customer.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    {customer.address && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{customer.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Projets */}
                  <div style={{ marginTop: 10, borderTop: '1px dashed var(--admin-border)', paddingTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <h5 style={{ margin: 0 }}>Projets ({getProjectsByCustomer(customer.id).length})</h5>
                      <button className="header-pill header-shape btn-accent has-tip" data-tip="Ajouter un projet" title="Ajouter un projet" aria-label="Ajouter un projet" onClick={() => { setSelectedCustomerForProject(customer); setEditingProject(null); setShowProjectForm(true); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                        </svg>
                      </button>
                    </div>
                    <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                      {getProjectsByCustomer(customer.id).map(project => (
                        <div key={project.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 10 }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{project.name || 'Sans nom'}</div>
                            <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>
                              {statusLabel(project.status)}{project.start_date ? ` · ${new Date(project.start_date).toLocaleDateString()}` : ''}{project.end_date ? ` → ${new Date(project.end_date).toLocaleDateString()}` : ''}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="header-pill header-shape" title="Voir le projet" aria-label="Voir le projet" onClick={() => { setDetailProject(project); setShowProjectDetail(true); }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                              </svg>
                            </button>
                            <button className="header-pill header-shape" title="Modifier" aria-label="Modifier" onClick={() => { setSelectedCustomerForProject(customer); setEditingProject(project); setShowProjectForm(true); }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                              </svg>
                            </button>
                            <button className="header-pill header-shape" title="Supprimer" aria-label="Supprimer" onClick={async () => { if (window.confirm('Supprimer ce projet ?')) { try { await pb.collection('projects').delete(project.id); await fetchProjects(); } catch (e) { alert(e?.message || 'Erreur suppression'); } } }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                      {getProjectsByCustomer(customer.id).length === 0 && (
                        <div style={{ fontSize: '.9rem', color: 'var(--admin-muted)' }}>Aucun projet pour ce client</div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    <button className="header-pill header-shape btn-muted has-tip" data-tip="Voir les détails" title="Voir les détails" aria-label="Voir les détails" onClick={() => { setDetailCustomer(customer); setShowCustomerDetail(true); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                      </svg>
                    </button>
                    <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                      <button className="header-pill header-shape btn-success has-tip" data-tip="Créer pour ce client" title="Créer pour ce client" aria-label="Créer pour ce client" onClick={(e) => { e.stopPropagation(); setShowAddMenuFor(p => p === customer.id ? null : customer.id); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                        </svg>
                      </button>
                      {showAddMenuFor === customer.id && (
                        <div className="add-menu" style={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 'auto', marginLeft: 0 }}>
                          <button className="add-menu-item" onClick={(e) => { e.stopPropagation(); setShowAddMenuFor(null); navigate(`/admin/facturation/devis?new=1&customer=${encodeURIComponent(customer.id)}`); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: .9 }}>
                              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                            </svg>
                            <span>Créer un devis</span>
                          </button>
                          <button className="add-menu-item" onClick={(e) => { e.stopPropagation(); setShowAddMenuFor(null); navigate(`/admin/facturation/factures?new=1&customer=${encodeURIComponent(customer.id)}`); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: .9 }}>
                              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                            </svg>
                            <span>Créer une facture</span>
                          </button>
                          <button className="add-menu-item" onClick={(e) => { e.stopPropagation(); setSelectedCustomerForProject(customer); setEditingProject(null); setShowProjectForm(true); setShowAddMenuFor(null); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: .9 }}>
                              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                            </svg>
                            <span>Créer un projet</span>
                          </button>
                        </div>
                      )}
                    </div>
                    <button className="header-pill header-shape btn-accent has-tip" data-tip="Modifier le client" title="Modifier" aria-label="Modifier" onClick={() => { setEditingCustomer(customer); setShowCustomerForm(true); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                      </svg>
                    </button>
                    <button className="header-pill header-shape btn-warning has-tip" data-tip="Réinitialiser MDP" title="Réinitialiser MDP" aria-label="Réinitialiser MDP" onClick={() => handlePasswordReset(customer)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
                      </svg>
                    </button>
                    <button className="header-pill header-shape btn-danger has-tip" data-tip="Supprimer le client" title="Supprimer" aria-label="Supprimer" onClick={() => deleteCustomer(customer.id)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                      </svg>
                    </button>
                  </div>
                  <div style={{ textAlign: 'center', color: 'var(--admin-muted)', fontSize: '.85rem', marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--admin-border)' }}>Créé le {formatDate(customer.created)}</div>
                </div>
              ))}

            {customers.filter(c => c.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) || (c.email && c.email.toLowerCase().includes(customerSearchTerm.toLowerCase())) || (c.phone && c.phone.includes(customerSearchTerm))).length === 0 && !adminLoading && allowEmpty && (
              <div style={{ gridColumn: '1 / -1', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400, width: '100%' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: .7, display: 'block' }}><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg>
                  <h3 style={{ margin: 0 }}>Aucun client trouvé</h3>
                  <p style={{ margin: 0, color: 'var(--admin-muted)' }}>
                    {customerSearchTerm ? `Aucun résultat pour "${customerSearchTerm}"` : 'Commencez par ajouter votre premier client'}
                  </p>
                  {!customerSearchTerm && (
                    <button
                      className="btn-primary"
                      onClick={() => { setEditingCustomer(null); setShowCustomerForm(true); }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        padding: '10px 14px', borderRadius: 10,
                        background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                        border: '1px solid rgba(79,70,229,0.6)',
                        boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                        color: 'white'
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      </span>
                      <span>Nouveau client</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Quick Project Form (reuse QuickVehicleForm for project creation) */}
      {showProjectForm && (
        <QuickVehicleForm
          customerId={selectedCustomerForProject?.id || ''}
          onSubmit={async (data) => {
            setLoading(true);
            try {
              // Pré-validation côté client pour messages immédiats
              const issues = [];
              if (!data?.brand && !data?.name) issues.push('Le nom du projet est requis');
              // Construire un payload conforme au schéma `projects`
              const allowedStatuses = ['draft', 'active', 'paused', 'done'];
              const allowedActions = ['Site web', 'E-commerce', 'SEO', 'SEA', 'Branding', 'Design', 'Développement', 'Maintenance', 'Formation', 'Licence', 'Autre'];
              const payload = {
                name: (data?.name || data?.brand || '').trim(),
                objectives: data?.objectives || data?.model || undefined,
                description: data?.description || data?.license_plate || undefined,
                status: data?.status && allowedStatuses.includes(data.status) ? data.status : 'draft',
                client: selectedCustomerForProject?.id || data?.client || data?.customer_id || data?.customer || undefined,
                internal_code: data?.internal_code || undefined,
                preview_url: data?.preview_url || undefined,
                preview_label: data?.preview_label || undefined,
                preview_embed: !!data?.preview_embed,
              };
              if (!payload.name) issues.push('Le champ "Nom du projet" ne peut pas être vide');
              if (!allowedStatuses.includes(payload.status)) issues.push('Le statut doit être parmi: brouillon, actif, en pause, terminé');
              // budgets
              const bmin = data?.budget_min ?? data?.mileage;
              const bmax = data?.budget_max ?? data?.year;
              if (bmin !== '' && bmin !== null && typeof bmin !== 'undefined') payload.budget_min = Number(bmin);
              if (bmax !== '' && bmax !== null && typeof bmax !== 'undefined') payload.budget_max = Number(bmax);
              if (typeof payload.budget_min === 'number' && Number.isNaN(payload.budget_min)) { delete payload.budget_min; issues.push('Budget min doit être un nombre'); }
              if (typeof payload.budget_max === 'number' && Number.isNaN(payload.budget_max)) { delete payload.budget_max; issues.push('Budget max doit être un nombre'); }
              if (typeof payload.budget_min === 'number' && typeof payload.budget_max === 'number' && payload.budget_max < payload.budget_min) {
                issues.push('Le budget max doit être supérieur ou égal au budget min');
              }
              // actions (select multi avec valeurs autorisées)
              let actions = data?.actions;
              if (!Array.isArray(actions)) {
                // QuickVehicleForm stocke parfois dans fuel_type (chaine CSV)
                const csv = data?.fuel_type || '';
                actions = csv.split(',').map(t => t.trim()).filter(Boolean);
              }
              if (Array.isArray(actions)) {
                const filtered = actions.filter(a => allowedActions.includes(a));
                if (filtered.length) payload.actions = filtered;
              }
              // Montrer messages de prévalidation si besoin
              if (issues.length) { alert(issues.join('\n')); setLoading(false); return; }
              if (editingProject?.id) {
                await pb.collection('projects').update(editingProject.id, payload);
              } else {
                // Assure un internal_code unique si fourni/vide
                if (!payload.internal_code) {
                  const ensureCode = () => `PRJ-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
                  payload.internal_code = ensureCode();
                  // Tentatives en cas de collision
                  let tries = 0;
                  while (tries < 3) {
                    try { await pb.collection('projects').create(payload); break; }
                    catch (e) {
                      const code = e?.data?.data?.internal_code?.code;
                      if (code === 'validation_not_unique') { payload.internal_code = ensureCode(); tries++; continue; }
                      throw e;
                    }
                  }
                  if (tries >= 3) throw new Error('Impossible de générer un code interne unique');
                } else {
                  await pb.collection('projects').create(payload);
                }
              }
              await fetchProjects();
              setShowProjectForm(false);
              setEditingProject(null);
            } catch (err) {
              // Construire un message détaillé depuis PocketBase
              const d = err?.data?.data || {};
              if (Object.keys(d).length) {
                const lines = [];
                if (d.name?.message) lines.push(`name: ${d.name.message}`);
                if (d.status?.message) lines.push(`status: ${d.status.message}`);
                if (d.client?.message) lines.push(`client: ${d.client.message}`);
                if (d.actions?.message) lines.push(`actions: ${d.actions.message}`);
                if (d.internal_code?.message) lines.push(`internal_code: ${d.internal_code.message}`);
                if (d.preview_url?.message) lines.push(`preview_url: ${d.preview_url.message}`);
                alert(`Erreurs de validation:\n${lines.join('\n')}`);
              } else {
                alert(err?.message || "Erreur d'enregistrement");
              }
            } finally {
              setLoading(false);
            }
          }}
          onCancel={() => { if (!loading) { setShowProjectForm(false); setEditingProject(null); } }}
          loading={loading}
          initialData={editingProject}
        />
      )}

      {showCustomerForm && (
        <QuickCustomerForm
          onSubmit={handleCreateOrUpdate}
          onCancel={() => { setShowCustomerForm(false); setEditingCustomer(null); }}
          loading={loading}
          initialData={editingCustomer}
        />
      )}

      {showQuoteForm && (
        <QuoteForm
          customers={customers}
          vehicles={projects}
          services={services}
          parts={products}
          loading={loading}
          initialData={editingQuote}
          onSubmit={async (data) => {
            setLoading(true);
            try {
              if (editingQuote?.id) await pb.collection('quotes').update(editingQuote.id, data);
              else await pb.collection('quotes').create(data);
              // Refresh detail view
              const qRes = await pb.collection('quotes').getList(1, 50, { sort: '-created', filter: `customer_id = "${detailCustomer.id}"` });
              setDetailQuotes(qRes?.items || []);
              setShowQuoteForm(false); setEditingQuote(null);
            } catch (e) { alert(e?.message); } finally { setLoading(false); }
          }}
          onCancel={() => { setShowQuoteForm(false); setEditingQuote(null); }}
        />
      )}

      {showInvoiceForm && (
        <InvoiceForm
          customers={customers}
          vehicles={projects}
          services={services}
          products={products}
          loading={loading}
          initialData={editingInvoice}
          onSubmit={async (data) => {
            setLoading(true);
            try {
              if (editingInvoice?.id) await pb.collection('invoices').update(editingInvoice.id, data);
              else await pb.collection('invoices').create(data);
              const res = await pb.collection('invoices').getList(1, 50, { sort: '-created', filter: `customer_id = "${detailCustomer.id}"` });
              setDetailInvoices(res?.items || []);
              setShowInvoiceForm(false); setEditingInvoice(null);
            } catch (e) { alert(e?.message); } finally { setLoading(false); }
          }}
          onCancel={() => { setShowInvoiceForm(false); setEditingInvoice(null); }}
        />
      )}

      {showContractForm && (
        <ContractForm
          customers={customers}
          projects={projects}
          loading={loading}
          initialData={editingContract}
          onSubmit={async (data) => {
            setLoading(true);
            try {
              if (editingContract?.id) await pb.collection('contracts').update(editingContract.id, data);
              else await pb.collection('contracts').create(data);
              const res = await pb.collection('contracts').getList(1, 50, { sort: '-created', filter: `customer_id = "${detailCustomer.id}"` });
              setDetailContracts(res?.items || []);
              setShowContractForm(false); setEditingContract(null);
            } catch (e) { alert(e?.message); } finally { setLoading(false); }
          }}
          onCancel={() => { setShowContractForm(false); setEditingContract(null); }}
        />
      )}

      {showQr && (
        <div className="modal-overlay" onClick={() => setShowQr(false)}>
          <div className="modal-content" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>QR Inscription</h3>
              <button className="close-btn" onClick={() => setShowQr(false)} type="button" aria-label="Fermer">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
            <div style={{ padding: 20, display: 'grid', gap: 12, justifyItems: 'center' }}>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUrl)}`} alt="QR Code" width={220} height={220} style={{ display: 'block', borderRadius: 12, border: '1px solid var(--admin-border)', background: 'white' }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="header-pill header-shape btn-accent" onClick={async () => { try { await navigator.clipboard.writeText(qrUrl); alert('Lien copié'); } catch { } }}>Copier le lien</button>
                <button className="header-pill header-shape" onClick={() => { try { const a = document.createElement('a'); a.href = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrUrl)}`; a.download = 'qr-inscription.png'; document.body.appendChild(a); a.click(); a.remove(); } catch { } }}>Télécharger QR</button>
              </div>
              <div style={{ wordBreak: 'break-all', color: 'var(--admin-muted)', textAlign: 'center' }}>{qrUrl}</div>
            </div>
          </div>
        </div>
      )}

      {showCustomerDetail && detailCustomer && (
        <div className="modal-overlay" onClick={() => { if (!loading) { setShowCustomerDetail(false); setDetailCustomer(null); } }}>
          <div className="modal-content" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Détails du client</h3>
              <button className="close-btn" onClick={() => { if (!loading) { setShowCustomerDetail(false); setDetailCustomer(null); } }} type="button" aria-label="Fermer">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
            <div style={{ padding: 20, display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 999, background: '#2d3556', display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 700 }}>
                  {detailCustomer?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{detailCustomer?.name}</div>
                  {detailCustomer?.company && <div style={{ color: 'var(--admin-muted)' }}>{detailCustomer.company}</div>}
                </div>
              </div>

              {detailCustomer?.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
                  <span>{detailCustomer.email}</span>
                </div>
              )}
              {detailCustomer?.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                  <span>{detailCustomer.phone}</span>
                </div>
              )}
              {detailCustomer?.address && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" /></svg>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{detailCustomer.address}</span>
                </div>
              )}

              <div style={{ paddingTop: 8 }}>
                <button
                  onClick={() => handlePasswordReset(detailCustomer)}
                  style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    color: '#d97706', // amber-600
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: '8px',
                    width: 'fit-content'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" /></svg>
                  Modifier le mot de passe
                </button>
              </div>

              <div style={{ borderTop: '1px solid var(--admin-border)', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Projets</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {getProjectsByCustomer(detailCustomer.id).map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{p.name || 'Sans nom'}</div>
                        <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>{statusLabel(p.status)}</div>
                      </div>
                      <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem' }}>{new Date(p.created).toLocaleDateString()}</div>
                    </div>
                  ))}
                  {getProjectsByCustomer(detailCustomer.id).length === 0 && (
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Aucun projet associé</div>
                  )}
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--admin-border)', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Devis ({detailQuotes.length})</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {detailQuotes.map(q => (
                    <div key={q.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{q.quote_number || '—'}</div>
                        <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>{q.status || 'draft'}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="header-pill header-shape" title="Télécharger PDF" onClick={() => handleDownloadQuote(q)}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                        </button>
                        <button className="header-pill header-shape" title="Modifier" onClick={() => { setEditingQuote(q); setShowQuoteForm(true); }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  {detailQuotes.length === 0 && (
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Aucun devis</div>
                  )}
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--admin-border)', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Factures ({detailInvoices.length})</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {detailInvoices.map(inv => (
                    <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{inv.invoice_number || '—'}</div>
                        <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>{inv.status || 'draft'}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="header-pill header-shape" title="Télécharger PDF" onClick={() => handleDownloadInvoice(inv)}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                        </button>
                        <button className="header-pill header-shape" title="Modifier" onClick={() => { setEditingInvoice(inv); setShowInvoiceForm(true); }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  {detailInvoices.length === 0 && (
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Aucune facture</div>
                  )}
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--admin-border)', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Rendez-vous ({detailAppointments.length})</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {detailAppointments.map(ap => (
                    <div key={ap.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{ap.title || 'Rendez-vous'}</div>
                        <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>{ap.status || '—'}</div>
                      </div>
                      <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem' }}>{ap.date ? new Date(ap.date).toLocaleDateString() : new Date(ap.created).toLocaleDateString()}</div>
                    </div>
                  ))}
                  {detailAppointments.length === 0 && (
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Aucun rendez-vous</div>
                  )}
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--admin-border)', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Contrats ({detailContracts.length})</div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {detailContracts.map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', border: '1px solid var(--admin-border)', borderRadius: 10 }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.contract_number || 'CONTRAT'}</div>
                        <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>{c.status || 'draft'}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="header-pill header-shape" title="Voir PDF" onClick={() => handleViewContract(c)}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" /></svg>
                        </button>
                        <button className="header-pill header-shape" title="Télécharger PDF" onClick={() => handleDownloadContract(c)}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                        </button>
                        <button className="header-pill header-shape" title="Modifier" onClick={() => { setEditingContract(c); setShowContractForm(true); }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  {detailContracts.length === 0 && (
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>Aucun contrat</div>
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button className="btn-secondary" type="button" onClick={() => { if (!loading) { setShowCustomerDetail(false); setDetailCustomer(null); } }}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showProjectDetail && detailProject && (
        <div className="modal-overlay" onClick={() => { if (!loading) { setShowProjectDetail(false); setDetailProject(null); } }}>
          <div className="modal-content" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Détails du projet</h3>
              <button className="close-btn" onClick={() => { if (!loading) { setShowProjectDetail(false); setDetailProject(null); } }} type="button" aria-label="Fermer">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
            <div style={{ padding: 20, display: 'grid', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{detailProject?.name || 'Sans nom'}</div>
                <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem' }}>
                  {statusLabel(detailProject?.status)}{detailProject?.start_date ? ` · ${new Date(detailProject.start_date).toLocaleDateString()}` : ''}{detailProject?.end_date ? ` → ${new Date(detailProject.end_date).toLocaleDateString()}` : ''}
                </div>
              </div>

              {detailProject?.internal_code && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v18H3z" /></svg>
                  <span>Code interne: {detailProject.internal_code}</span>
                </div>
              )}

              {(detailProject?.budget_min != null || detailProject?.budget_max != null) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5v14m7-7H5" /></svg>
                  <span>
                    Budget: {detailProject?.budget_min != null ? `${Number(detailProject.budget_min).toLocaleString('fr-FR')}€` : '—'}
                    {detailProject?.budget_max != null ? ` – ${Number(detailProject.budget_max).toLocaleString('fr-FR')}€` : ''}
                  </span>
                </div>
              )}

              {Array.isArray(detailProject?.actions) && detailProject.actions.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Actions</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {detailProject.actions.map((a, idx) => (
                      <span key={idx} style={{ padding: '4px 8px', border: '1px solid var(--admin-border)', borderRadius: 999 }}>{a}</span>
                    ))}
                  </div>
                </div>
              )}

              {detailProject?.objectives && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Objectifs</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{detailProject.objectives}</div>
                </div>
              )}

              {detailProject?.description && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Description</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{detailProject.description}</div>
                </div>
              )}

              {detailProject?.preview_url && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Maquette / Prévisualisation</div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <a className="header-pill header-shape btn-accent" href={detailProject.preview_url} target="_blank" rel="noreferrer">
                        {detailProject.preview_label || 'Voir la maquette'}
                      </a>
                      <button className="header-pill header-shape" onClick={async () => { try { await navigator.clipboard.writeText(detailProject.preview_url); alert('URL copiée'); } catch { alert('Copie indisponible'); } }}>
                        Copier l’URL
                      </button>
                      <button className="header-pill header-shape" onClick={async () => {
                        try {
                          if (navigator.share) {
                            await navigator.share({ title: detailProject.preview_label || detailProject.name || 'Maquette', text: 'Découvrez la maquette du projet', url: detailProject.preview_url });
                          } else {
                            await navigator.clipboard.writeText(detailProject.preview_url);
                            alert('Lien copié (partage non supporté)');
                          }
                        } catch { }
                      }}>
                        Partager
                      </button>
                      <a className="header-pill header-shape" href={detailProject.preview_url} target="_blank" rel="noreferrer">
                        Ouvrir en pleine page
                      </a>
                    </div>
                    {detailProject.preview_embed ? (
                      <div style={{ border: '1px solid var(--admin-border)', borderRadius: 8, overflow: 'hidden' }}>
                        {(() => {
                          const url = String(detailProject.preview_url || '');
                          const isMixed = typeof window !== 'undefined' && window.location?.protocol === 'https:' && url.startsWith('http://');
                          if (isMixed) {
                            return (
                              <div style={{ padding: 12 }}>
                                <div style={{ fontWeight: 600, marginBottom: 6 }}>Prévisualisation non disponible en HTTPS</div>
                                <div style={{ fontSize: '.9rem', color: 'var(--admin-muted)' }}>
                                  Le lien commence par http:// et est bloqué par le navigateur dans une page sécurisée (https).
                                  Utilisez « Ouvrir en pleine page » ci‑dessus ou remplacez l’URL par une version https:// si possible.
                                </div>
                              </div>
                            );
                          }
                          return (
                            <iframe
                              title={`preview-${detailProject.id}`}
                              src={url}
                              style={{ width: '100%', height: 420, background: '#fff' }}
                              loading="lazy"
                              allow="fullscreen; clipboard-read; clipboard-write"
                              referrerPolicy="no-referrer-when-downgrade"
                              allowFullScreen
                            />
                          );
                        })()}
                        <div style={{ padding: 8, fontSize: '.85rem', color: 'var(--admin-muted)', borderTop: '1px dashed var(--admin-border)' }}>
                          Si la prévisualisation ne s’affiche pas, le site distant bloque l’intégration en iframe.
                          Utilisez « Ouvrir en pleine page » pour l’afficher dans un nouvel onglet.
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button className="btn-secondary" type="button" onClick={() => { if (!loading) { setShowProjectDetail(false); setDetailProject(null); } }}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Password Reset Modal */}
      {showPwdModal && (
        <div className="modal-overlay" onClick={() => setShowPwdModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500, padding: 30 }}>
            {pwdStep === 'input' && (
              <>
                <h3 style={{ marginBottom: 16 }}>Nouveau mot de passe</h3>
                <p style={{ color: 'var(--admin-muted)', marginBottom: 20 }}>
                  Définissez un nouveau mot de passe pour <strong>{pwdTargetUser?.name}</strong>.
                  <br />Vous devrez le transmettre manuellement au client.
                </p>

                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Mot de passe (min 8 car.)</label>
                  <input
                    type="text"
                    autoFocus
                    value={pwdNewPassword}
                    onChange={e => setPwdNewPassword(e.target.value)}
                    style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid var(--admin-border)', background: 'var(--admin-panel-2)', color: 'var(--admin-text)' }}
                    placeholder="Ex: InfoColab2024!"
                  />
                </div>

                {pwdError && <div style={{ color: '#ef4444', marginBottom: 16, background: 'rgba(239,68,68,0.1)', padding: 10, borderRadius: 6 }}>{pwdError}</div>}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button className="btn-secondary" onClick={() => setShowPwdModal(false)}>Annuler</button>
                  <button className="btn-primary" disabled={pwdLoading || !pwdNewPassword} onClick={executePasswordReset}>
                    {pwdLoading ? 'Modification...' : 'Modifier'}
                  </button>
                </div>
              </>
            )}

            {pwdStep === 'superadmin' && (
              <>
                <h3 style={{ marginBottom: 16, color: '#eab308', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" /></svg>
                  Blocage de Sécurité
                </h3>
                <p style={{ color: 'var(--admin-muted)', marginBottom: 20, fontSize: '.95rem', lineHeight: 1.5 }}>
                  PocketBase ne permet pas de modifier le mot de passe d'un utilisateur sans l'ancien, sauf pour les <strong>Super-Admins</strong>.
                  <br /><br />
                  Veuillez entrer vos identifiants du <strong>Dashboard PocketBase</strong> (Admin système) pour forcer cette modification.
                </p>

                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6 }}>Email Super-Admin</label>
                  <input
                    type="email"
                    value={pwdAdminEmail}
                    onChange={e => setPwdAdminEmail(e.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--admin-border)', background: 'var(--admin-panel-2)', color: 'var(--admin-text)' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 6 }}>Mot de passe Super-Admin</label>
                  <input
                    type="password"
                    value={pwdAdminPass}
                    onChange={e => setPwdAdminPass(e.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--admin-border)', background: 'var(--admin-panel-2)', color: 'var(--admin-text)' }}
                  />
                </div>

                {pwdError && <div style={{ color: '#ef4444', marginBottom: 16, background: 'rgba(239,68,68,0.1)', padding: 10, borderRadius: 6 }}>{pwdError}</div>}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button className="btn-secondary" onClick={() => setShowPwdModal(false)}>Annuler</button>
                  <button className="btn-primary" disabled={pwdLoading} onClick={executeSuperAdminReset} style={{ background: '#eab308' }}>
                    {pwdLoading ? 'Forçage...' : 'Forcer la modification'}
                  </button>
                </div>
              </>
            )}

            {pwdStep === 'success' && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#22c55e', color: 'white', display: 'grid', placeItems: 'center', margin: '0 auto 20px' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <h3 style={{ marginBottom: 16 }}>Mot de passe modifié !</h3>
                <p style={{ color: 'var(--admin-muted)', marginBottom: 24 }}>
                  Le mot de passe de <strong>{pwdTargetUser?.name}</strong> a été mis à jour.
                </p>

                <div style={{ background: 'var(--admin-panel-2)', padding: 16, borderRadius: 8, border: '1px solid var(--admin-border)', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <code style={{ fontSize: '1.1rem', fontWeight: 600 }}>{pwdNewPassword}</code>
                  <button className="header-pill" onClick={() => { navigator.clipboard.writeText(pwdNewPassword); alert('Copié !'); }}>Copier</button>
                </div>

                <button className="btn-primary" style={{ width: '100%' }} onClick={() => setShowPwdModal(false)}>Fermer</button>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminBillingCustomersPage;
